/**
 * InflowPay Payment SDK
 *
 * Minimal SDK wrapper for CardElement functionality
 */


import type {
  SDKConfig,
  CardElementOptions,
  PaymentStatusResponse,
} from '../types';
import { detectBrowserLocale } from '../i18n';
import { CardElement } from '../ui/card-element';
import { PaymentClient } from '../ui/card-element/payment-client';

export class PaymentSDK {
  private config: SDKConfig & { environment: 'sandbox' | 'production' | 'development'; timeout: number; debug: boolean };

  /**
   * Initialize the InflowPay Payment SDK
   *
   * @param config - SDK configuration options
   * @param config.apiKey - InflowPay public API key (required, must start with "inflow_pub_")
   * @param config.timeout - Request timeout in milliseconds (defaults to 30000)
   * @param config.debug - Enable debug logging (defaults to false)
   *
   * @throws {Error} If API key is missing or invalid
   *
   * @example
   * ```typescript
   * // Production environment (auto-detected from key prefix)
   * const sdk = new PaymentSDK({
   *   apiKey: 'inflow_pub_prod_xxx'
   * });
   *
   * // Development environment (auto-detected from key prefix)
   * const sdk = new PaymentSDK({
   *   apiKey: 'inflow_pub_dev_xxx'
   * });
   *
   * // Sandbox/local development (auto-detected from key prefix)
   * const sdk = new PaymentSDK({
   *   apiKey: 'inflow_pub_local_xxx'
   * });
   * ```
   */
  constructor(config: SDKConfig) {
    // Validate API key
    if (!config.apiKey || typeof config.apiKey !== 'string') {
      throw new Error('API key is required');
    }

    // Accept production, development, preprod, and local keys
    if (!config.apiKey.startsWith('inflow_pub_') &&
      !config.apiKey.startsWith('inflow_local_') &&
      !config.apiKey.startsWith('inflow_preprod_')) {
      throw new Error('Invalid API key format. Keys must start with "inflow_pub_", "inflow_preprod_", or "inflow_local_"');
    }

    // Auto-detect environment from API key prefix
    // inflow_pub_local_xxx or inflow_local_xxx -> sandbox (localhost)
    // inflow_pub_dev_xxx or inflow_pub_preprod_xxx -> development (dev/preprod server)
    // inflow_pub_prod_xxx -> production
    let environment: 'sandbox' | 'production' | 'development';

    // Check _local_ first to avoid confusion with _dev_ in "local_dev_" strings
    if (config.apiKey.includes('_local_') || config.apiKey.startsWith('inflow_local_')) {
      environment = 'sandbox';
    } else if (config.apiKey.includes('_prod_') && !config.apiKey.includes('_preprod_')) {
      // Check for _prod_ but exclude _preprod_ (which should be dev)
      environment = 'production';
    } else if (config.apiKey.includes('_dev_') || config.apiKey.includes('_preprod_') || config.apiKey.startsWith('inflow_preprod_')) {
      environment = 'development';
    } else {
      // Default to sandbox for safety (no real money)
      environment = 'sandbox';
    }

    // Set config with defaults
    this.config = {
      apiKey: config.apiKey,
      environment,
      locale: config.locale || detectBrowserLocale(),
      timeout: config.timeout || 30000,
      debug: config.debug || false,
    };

    this.log('[PaymentSDK] Initialized', {
      environment: this.config.environment,
      apiUrl: PaymentClient.getDefaultApiUrl(environment),
    });
  }

  /**
   * Create a CardElement for embedded payment UI
   *
   * @param options - CardElement configuration
   * @returns CardElement instance
   *
   * @example
   * ```typescript
   * const cardElement = sdk.createCardElement({
   *   container: '#card-container',
   *   paymentId: 'pay_123',
   *   onComplete: (result) => console.log('Payment complete:', result)
   * });
   *
   * cardElement.mount();
   * ```
   */
  createCardElement(options: CardElementOptions): CardElement {
    return new CardElement(this.config, options);
  }

  /**
   * Get the current status of a payment
   *
   * Useful for polling payment status after 3DS authentication or
   * checking the final state of a payment after redirect.
   *
   * @param paymentId - The payment ID to check
   * @returns Payment status information
   *
   * @example
   * ```typescript
   * // After user returns from 3DS authentication
   * const status = await sdk.getPaymentStatus('pay_123');
   *
   * if (status.status === 'PAYMENT_SUCCESS') {
   *   console.log('Payment completed!');
   * } else if (status.status === 'PAYMENT_FAILED') {
   *   console.log('Payment failed:', status.error);
   * }
   * ```
   */
  async getPaymentStatus(paymentId: string): Promise<PaymentStatusResponse> {
    this.log('[PaymentSDK] Checking payment status:', paymentId);

    // Determine the API URL based on environment
    const apiUrl = PaymentClient.getDefaultApiUrl(this.config.environment);

    const response = await fetch(
      `${apiUrl}/sdk/payment/${paymentId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Inflow-Public-Key': this.config.apiKey,
        },
        signal: AbortSignal.timeout(this.config.timeout),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to get payment status');
    }

    const data = await response.json();
    this.log('[PaymentSDK] Payment status:', data);
    return data;
  }

  /**
   * Internal logging (respects debug flag)
   */
  private log(message: string, data?: any): void {
    if (this.config.debug) {
      if (data !== undefined) {
        console.log(message, data);
      } else {
        console.log(message);
      }
    }
  }
}